import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import emailService, { MailboxImportResult } from '../../services/emailService';

interface Props {
  onComplete: () => void;
  onCancel: () => void;
}

const MailboxBulkUpload: React.FC<Props> = ({ onComplete, onCancel }) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [testConnections, setTestConnections] = useState(true);
  const [duplicateHandling, setDuplicateHandling] = useState<'skip' | 'update'>('skip');
  const [result, setResult] = useState<MailboxImportResult | null>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && (droppedFile.type === 'text/csv' || droppedFile.name.endsWith('.csv'))) {
      setFile(droppedFile);
    } else {
      toast.error('Please upload a CSV file');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await emailService.downloadMailboxTemplate();
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setIsUploading(true);
    try {
      const importResult = await emailService.importMailboxes(file, {
        testConnections,
        duplicateHandling
      });
      
      setResult(importResult);
      
      if (importResult.failed > 0) {
        toast.error(`Import completed with ${importResult.failed} failures`);
      } else {
        toast.success(`Successfully imported ${importResult.created} mailboxes`);
      }
    } catch (error: any) {
      toast.error(error.message || 'Import failed');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (result && result.created > 0) {
      onComplete();
    } else {
      onCancel();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Bulk Import Mailboxes</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {!result ? (
            <>
              {/* Template Download */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <h3 className="font-semibold text-blue-900 mb-1">Need a template?</h3>
                    <p className="text-sm text-blue-800 mb-2">
                      Download our CSV template with example mailboxes and required columns.
                    </p>
                    <button
                      onClick={handleDownloadTemplate}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download Template CSV
                    </button>
                  </div>
                </div>
              </div>

              {/* File Upload Area */}
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                
                {file ? (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                    <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                  </div>
                ) : (
                  <p className="text-gray-600 mb-4">
                    Drag and drop your CSV file here, or click to browse
                  </p>
                )}

                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors"
                >
                  {file ? 'Change File' : 'Select File'}
                </label>
              </div>

              {/* Options */}
              <div className="mt-6 space-y-4">
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="test-connections"
                    checked={testConnections}
                    onChange={(e) => setTestConnections(e.target.checked)}
                    className="h-4 w-4 text-blue-600 rounded border-gray-300 mt-0.5"
                  />
                  <label htmlFor="test-connections" className="ml-2 text-sm text-gray-700">
                    <span className="font-medium">Test SMTP connections before importing</span>
                    <span className="block text-gray-500 text-xs mt-0.5">
                      Recommended but slower. Verifies credentials before creating mailboxes.
                    </span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duplicate Handling:
                  </label>
                  <div className="space-y-2">
                    <div className="flex items-start">
                      <input
                        type="radio"
                        id="skip-duplicates"
                        name="duplicate-handling"
                        value="skip"
                        checked={duplicateHandling === 'skip'}
                        onChange={() => setDuplicateHandling('skip')}
                        className="h-4 w-4 text-blue-600 mt-0.5"
                      />
                      <label htmlFor="skip-duplicates" className="ml-2 text-sm text-gray-700">
                        <span className="font-medium">Skip existing mailboxes</span>
                        <span className="block text-gray-500 text-xs mt-0.5">
                          Don't import mailboxes that already exist
                        </span>
                      </label>
                    </div>
                    <div className="flex items-start">
                      <input
                        type="radio"
                        id="update-duplicates"
                        name="duplicate-handling"
                        value="update"
                        checked={duplicateHandling === 'update'}
                        onChange={() => setDuplicateHandling('update')}
                        className="h-4 w-4 text-blue-600 mt-0.5"
                      />
                      <label htmlFor="update-duplicates" className="ml-2 text-sm text-gray-700">
                        <span className="font-medium">Update existing mailboxes</span>
                        <span className="block text-gray-500 text-xs mt-0.5">
                          Overwrite existing mailbox settings with new data
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={onCancel}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={!file || isUploading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                >
                  {isUploading && (
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  )}
                  {isUploading ? 'Importing...' : 'Import Mailboxes'}
                </button>
              </div>
            </>
          ) : (
            /* Results Display */
            <div>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-green-700">{result.created}</div>
                  <div className="text-sm text-green-600">Created</div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-blue-700">{result.updated}</div>
                  <div className="text-sm text-blue-600">Updated</div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-gray-700">{result.skipped}</div>
                  <div className="text-sm text-gray-600">Skipped</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-red-700">{result.failed}</div>
                  <div className="text-sm text-red-600">Failed</div>
                </div>
              </div>

              {/* Detailed Results */}
              {result.results.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto max-h-96">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Row</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {result.results.map((row, index) => (
                          <tr key={index} className={row.status === 'failed' || row.status === 'error' ? 'bg-red-50' : ''}>
                            <td className="px-4 py-3 text-sm text-gray-900">{row.row}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{row.email}</td>
                            <td className="px-4 py-3 text-sm">
                              {row.status === 'created' && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  ✓ Created
                                </span>
                              )}
                              {row.status === 'updated' && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  ↻ Updated
                                </span>
                              )}
                              {row.status === 'skipped' && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  ⏭ Skipped
                                </span>
                              )}
                              {(row.status === 'failed' || row.status === 'error') && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  ✗ Failed
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {row.error || row.reason || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Close Button */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleClose}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {result.created > 0 ? 'View Mailboxes' : 'Close'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MailboxBulkUpload;
